<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto"
    @click.self="close"
  >
    <div class="bg-white rounded-t-2xl sm:rounded-lg w-full max-w-3xl p-4 sm:p-6 my-0 sm:my-8 max-h-[92vh] overflow-y-auto shadow-xl">
      <div class="flex items-start justify-between gap-3 mb-4">
        <div class="min-w-0 flex-1">
          <input
            v-if="form"
            v-model="form.title"
            class="w-full text-lg font-semibold text-gray-900 border-0 border-b border-transparent hover:border-gray-200 focus:border-blue-400 focus:ring-0 px-0 py-1"
            :placeholder="$t('smm.board.ticket_title')"
            @blur="saveField('title')"
          >
          <div
            v-else
            class="h-8 w-2/3 rounded bg-gray-100 animate-pulse"
          />
        </div>
        <button
          type="button"
          class="shrink-0 text-gray-400 hover:text-gray-700 text-xl leading-none"
          :aria-label="$t('common.close')"
          @click="close"
        >
          ×
        </button>
      </div>

      <p v-if="error" class="text-sm text-red-600 mb-3 whitespace-pre-line">{{ error }}</p>
      <div v-if="loading" class="py-10 text-center text-gray-500">{{ $t('common.loading') }}</div>

      <template v-else-if="form">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label class="text-xs text-gray-500">{{ $t('smm.board.project') }}</label>
            <select
              v-model="form.project_id"
              class="w-full border rounded-lg p-2 mt-1 text-sm"
              @change="saveField('project_id')"
            >
              <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div>
            <label class="text-xs text-gray-500">{{ $t('smm.board.status') }}</label>
            <select
              v-model="form.status_id"
              class="w-full border rounded-lg p-2 mt-1 text-sm"
              @change="saveField('status_id')"
            >
              <option v-for="s in statuses" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div>
            <label class="text-xs text-gray-500">{{ $t('smm.board.type') }}</label>
            <select
              v-model="form.type_id"
              class="w-full border rounded-lg p-2 mt-1 text-sm"
              @change="saveField('type_id')"
            >
              <option :value="null">—</option>
              <option v-for="t in types" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
          <div>
            <label class="text-xs text-gray-500">{{ $t('smm.board.priority') }}</label>
            <select
              v-model="form.priority"
              class="w-full border rounded-lg p-2 mt-1 text-sm"
              @change="saveField('priority')"
            >
              <option v-for="p in priorities" :key="p" :value="p">
                {{ $t(`smm.board.priority_${p}`) }}
              </option>
            </select>
          </div>
          <div>
            <label class="text-xs text-gray-500">{{ $t('smm.board.due_date') }}</label>
            <input
              v-model="form.due_date"
              type="date"
              class="w-full border rounded-lg p-2 mt-1 text-sm"
              @change="saveField('due_date')"
            >
          </div>
        </div>

        <div class="mb-5">
          <label class="text-xs text-gray-500">{{ $t('smm.board.description') }}</label>
          <textarea
            v-model="form.description"
            rows="4"
            class="w-full border rounded-lg p-2.5 mt-1 text-sm resize-y"
            :placeholder="$t('smm.board.description_placeholder')"
            @blur="saveField('description')"
          />
        </div>

        <div class="mb-5 space-y-3">
          <h4 class="text-sm font-semibold text-gray-800">{{ $t('smm.board.sections') }}</h4>
          <div
            v-for="section in form.sections || []"
            :key="section.id"
            class="rounded-lg border border-gray-200 p-3 bg-gray-50/60"
          >
            <input
              v-model="section.name"
              class="w-full text-sm font-medium bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-400 focus:ring-0 px-0 py-0.5 mb-2"
              @blur="saveSection(section, 'name')"
            >
            <p
              v-if="section.definition?.description"
              class="text-xs text-gray-400 mb-1"
            >{{ section.definition.description }}</p>
            <textarea
              v-model="section.body"
              rows="3"
              class="w-full border rounded-lg p-2 text-sm resize-y bg-white"
              :placeholder="$t('smm.board.section_placeholder')"
              @blur="saveSection(section, 'body')"
            />
          </div>
          <p v-if="!(form.sections || []).length" class="text-sm text-gray-500">
            {{ $t('smm.board.sections_empty') }}
          </p>
        </div>

        <div class="mb-5">
          <h4 class="text-sm font-semibold text-gray-800 mb-2">{{ $t('smm.board.links') }}</h4>
          <div class="space-y-2 mb-2">
            <div
              v-for="link in form.links || []"
              :key="link.id"
              class="rounded-lg border border-gray-200 bg-white p-2"
            >
              <div
                v-if="editingLinkId === link.id"
                class="flex flex-col sm:flex-row gap-2"
              >
                <input
                  v-model="editingLink.title"
                  class="sm:w-1/3 border rounded-lg p-2 text-sm"
                  :placeholder="$t('smm.board.link_title_placeholder')"
                >
                <input
                  v-model="editingLink.url"
                  class="flex-1 border rounded-lg p-2 text-sm font-mono"
                  :placeholder="$t('smm.board.link_url_placeholder')"
                  @keydown.enter.prevent="saveEditingLink"
                >
                <div class="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    class="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
                    :disabled="!editingLink.url.trim() || linking"
                    :title="$t('common.save')"
                    @click="saveEditingLink"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="p-2 rounded-lg text-gray-400 hover:bg-gray-100"
                    :title="$t('common.cancel')"
                    @click="cancelEditLink"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div
                v-else
                class="flex items-center gap-2"
              >
                <a
                  :href="hrefFor(link.url)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex-1 min-w-0 text-sm text-blue-600 hover:underline truncate"
                  :title="link.url"
                >
                  <span v-if="link.title" class="font-medium text-gray-800 hover:text-blue-600">{{ link.title }}</span>
                  <span v-else class="font-mono text-xs">{{ link.url }}</span>
                  <span
                    v-if="link.title"
                    class="block text-xs font-mono text-gray-400 truncate"
                  >{{ link.url }}</span>
                </a>
                <button
                  type="button"
                  class="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                  :title="$t('common.edit')"
                  @click="startEditLink(link)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 7.125L16.875 4.5" />
                  </svg>
                </button>
                <button
                  type="button"
                  class="shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                  :title="$t('common.delete')"
                  @click="removeLink(link)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                  </svg>
                </button>
              </div>
            </div>
            <p v-if="!(form.links || []).length" class="text-sm text-gray-500">
              {{ $t('smm.board.links_empty') }}
            </p>
          </div>
          <div class="flex flex-col sm:flex-row gap-2">
            <input
              v-model="newLink.title"
              class="sm:w-1/3 border rounded-lg p-2 text-sm"
              :placeholder="$t('smm.board.link_title_placeholder')"
            >
            <input
              v-model="newLink.url"
              class="flex-1 border rounded-lg p-2 text-sm font-mono"
              :placeholder="$t('smm.board.link_url_placeholder')"
              @keydown.enter.prevent="addLink"
            >
            <button
              type="button"
              class="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              :disabled="!newLink.url.trim() || linking"
              @click="addLink"
            >
              {{ $t('smm.board.link_add') }}
            </button>
          </div>
        </div>

        <div class="mb-5">
          <h4 class="text-sm font-semibold text-gray-800 mb-2">{{ $t('smm.board.linked_tickets') }}</h4>
          <div class="space-y-2 mb-2">
            <div
              v-for="item in form.linked_tickets || []"
              :key="item.relation_id"
              class="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2.5"
            >
              <button
                type="button"
                class="flex-1 min-w-0 text-left"
                @click="openLinked(item.ticket.id)"
              >
                <div class="flex items-center gap-2 mb-0.5">
                  <span class="text-[10px] text-gray-400">#{{ item.ticket.id }}</span>
                  <span
                    v-if="item.type"
                    class="text-[10px] uppercase px-1.5 py-0.5 rounded"
                    :style="{
                      backgroundColor: (item.type.color || '#94a3b8') + '22',
                      color: item.type.color || '#64748b',
                    }"
                  >{{ item.type.name }}</span>
                  <span
                    v-if="item.status"
                    class="text-[10px] text-gray-500"
                  >{{ item.status.name }}</span>
                </div>
                <p class="text-sm font-medium text-gray-900 truncate">{{ item.ticket.title }}</p>
              </button>
              <button
                type="button"
                class="shrink-0 text-xs text-red-500 hover:underline"
                @click="removeRelation(item)"
              >{{ $t('common.delete') }}</button>
            </div>
            <p v-if="!(form.linked_tickets || []).length" class="text-sm text-gray-500">
              {{ $t('smm.board.linked_tickets_empty') }}
            </p>
          </div>
          <div class="relative">
            <input
              v-model="ticketSearch"
              class="w-full border rounded-lg p-2 text-sm"
              :placeholder="$t('smm.board.linked_search_placeholder')"
              @input="onTicketSearch"
            >
            <div
              v-if="ticketSearchResults.length"
              class="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
            >
              <button
                v-for="t in ticketSearchResults"
                :key="t.id"
                type="button"
                class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-50 last:border-0"
                @click="linkTicket(t)"
              >
                <span class="text-gray-400 text-xs">#{{ t.id }}</span>
                {{ t.title }}
                <span v-if="t.status" class="text-xs text-gray-400 ml-1">· {{ t.status.name }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="mb-5">
          <div class="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h4 class="text-sm font-semibold text-gray-800">{{ $t('smm.board.attachments') }}</h4>
            <div class="flex flex-wrap items-center gap-2">
              <button
                type="button"
                class="px-3 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-gray-50"
                @click="pasteAttachmentsFromClipboard"
              >
                {{ $t('smm.upload.paste_clipboard') }}
              </button>
              <label class="px-3 py-1.5 text-xs rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50">
                {{ $t('smm.board.upload') }}
                <input ref="attachInput" type="file" multiple class="hidden" @change="onUpload" />
              </label>
            </div>
          </div>
          <div
            ref="attachZone"
            class="border-2 border-dashed rounded-lg p-3 text-center transition outline-none mb-2"
            :class="attachZoneClass"
            tabindex="0"
            @focus="attachFocused = true"
            @blur="attachFocused = false"
            @paste="onAttachPaste"
            @dragover.prevent="attachDragOver = true"
            @dragleave.prevent="attachDragOver = false"
            @drop.prevent="onAttachDrop"
            @click="focusAttachZone"
          >
            <p class="text-sm text-gray-600">{{ $t('smm.board.attachments_drop_hint') }}</p>
            <p class="text-xs text-gray-400 mt-1">{{ $t('smm.upload.ctrl_v_hint') }}</p>
            <p v-if="uploadingFiles" class="text-xs text-blue-600 mt-2">{{ $t('common.saving') }}</p>
          </div>
          <div v-if="(form.files || []).length" class="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div
              v-for="file in form.files"
              :key="file.id"
              class="relative group rounded-lg border border-gray-200 overflow-hidden bg-white"
            >
              <a :href="file.url" target="_blank" rel="noopener" class="block">
                <img
                  v-if="file.type === 'image'"
                  :src="file.url"
                  :alt="file.original_name || ''"
                  class="w-full h-24 object-cover"
                >
                <div v-else class="h-24 flex items-center justify-center text-xs text-gray-500 p-2 text-center">
                  {{ file.original_name || 'file' }}
                </div>
              </a>
              <button
                type="button"
                class="absolute top-1 right-1 w-6 h-6 rounded bg-black/60 text-white text-xs opacity-0 group-hover:opacity-100"
                @click="removeFile(file)"
              >×</button>
            </div>
          </div>
          <p v-else class="text-sm text-gray-500">{{ $t('smm.board.attachments_empty') }}</p>
        </div>

        <div class="mb-2">
          <h4 class="text-sm font-semibold text-gray-800 mb-2">{{ $t('smm.board.comments') }}</h4>
          <div class="space-y-2 mb-3 max-h-48 overflow-y-auto">
            <div
              v-for="comment in form.comments || []"
              :key="comment.id"
              class="rounded-lg border border-gray-100 bg-gray-50 p-2.5 text-sm"
            >
              <div class="flex items-center justify-between gap-2 mb-1">
                <span class="text-xs font-medium text-gray-700">
                  {{ comment.user?.name || $t('smm.board.anonymous') }}
                </span>
                <div class="flex items-center gap-2">
                  <span class="text-[10px] text-gray-400">{{ formatDate(comment.created_at) }}</span>
                  <button
                    type="button"
                    class="text-[10px] text-red-500 hover:underline"
                    @click="removeComment(comment)"
                  >{{ $t('common.delete') }}</button>
                </div>
              </div>
              <p class="text-gray-800 whitespace-pre-wrap">{{ comment.body }}</p>
            </div>
            <p v-if="!(form.comments || []).length" class="text-sm text-gray-500">
              {{ $t('smm.board.comments_empty') }}
            </p>
          </div>
          <div class="flex gap-2">
            <textarea
              v-model="newComment"
              rows="2"
              class="flex-1 border rounded-lg p-2 text-sm resize-y"
              :placeholder="$t('smm.board.comment_placeholder')"
            />
            <button
              type="button"
              class="self-end px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              :disabled="!newComment.trim() || commenting"
              @click="addComment"
            >
              {{ $t('smm.board.comment_add') }}
            </button>
          </div>
        </div>

        <div class="flex justify-between gap-2 pt-4 border-t border-gray-100 mt-4">
          <button
            type="button"
            class="px-3 py-2 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
            @click="deleteTicket"
          >
            {{ $t('common.delete') }}
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            @click="close"
          >
            {{ $t('common.close') }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    show: { type: Boolean, default: false },
    ticketId: { type: [Number, String], default: null },
    statuses: { type: Array, default: () => [] },
    types: { type: Array, default: () => [] },
    projects: { type: Array, default: () => [] },
  },
  emits: ['close', 'updated', 'deleted', 'open-ticket'],
  data: () => ({
    loading: false,
    saving: false,
    commenting: false,
    linking: false,
    uploadingFiles: false,
    attachFocused: false,
    attachDragOver: false,
    error: null,
    form: null,
    newComment: '',
    newLink: { url: '', title: '' },
    editingLinkId: null,
    editingLink: { url: '', title: '' },
    ticketSearch: '',
    ticketSearchResults: [],
    searchTimer: null,
    priorities: ['low', 'medium', 'high', 'urgent'],
  }),
  watch: {
    show: {
      immediate: true,
      handler(v) {
        if (v && this.ticketId) {
          this.load()
          window.addEventListener('paste', this.onWindowAttachPaste)
        } else {
          window.removeEventListener('paste', this.onWindowAttachPaste)
        }
      },
    },
    ticketId(v) {
      if (this.show && v) this.load()
    },
  },
  beforeUnmount() {
    window.removeEventListener('paste', this.onWindowAttachPaste)
  },
  computed: {
    attachZoneClass() {
      if (this.attachDragOver) return 'border-blue-500 bg-blue-50'
      if (this.attachFocused) return 'border-blue-400 bg-blue-50/50 ring-2 ring-blue-200'
      return 'border-gray-300 bg-gray-50/50 hover:border-gray-400'
    },
  },
  methods: {
    formatValidationError(e) {
      const data = e.response?.data
      if (!data) return null
      if (data.message) return data.message
      if (data.errors) return Object.values(data.errors).flat().join('\n')
      return null
    },
    formatDate(value) {
      if (!value) return ''
      try {
        return new Date(value).toLocaleString(this.$i18n.locale === 'ru' ? 'ru-RU' : 'en-US', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch (_) {
        return String(value)
      }
    },
    close() {
      this.$emit('close')
    },
    hrefFor(url) {
      const value = String(url || '').trim()
      if (!value) return '#'
      if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return value
      return `https://${value}`
    },
    openLinked(id) {
      if (!id || id === this.form?.id) return
      this.$emit('open-ticket', id)
    },
    async load() {
      this.loading = true
      this.error = null
      this.form = null
      this.ticketSearch = ''
      this.ticketSearchResults = []
      this.newLink = { url: '', title: '' }
      this.editingLinkId = null
      this.editingLink = { url: '', title: '' }
      try {
        const { data } = await axios.get(`/api/v1/smm/board/tickets/${this.ticketId}`)
        const ticket = data.data
        this.form = {
          ...ticket,
          due_date: ticket.due_date ? String(ticket.due_date).slice(0, 10) : '',
          type_id: ticket.type_id ?? null,
          sections: (ticket.sections || []).map((s) => ({ ...s })),
          links: (ticket.links || []).map((l) => ({ ...l })),
          linked_tickets: ticket.linked_tickets || [],
        }
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.loading = false
      }
    },
    applyTicket(ticket) {
      this.form = {
        ...ticket,
        due_date: ticket.due_date ? String(ticket.due_date).slice(0, 10) : '',
        type_id: ticket.type_id ?? null,
        sections: (ticket.sections || []).map((s) => ({ ...s })),
        links: (ticket.links || []).map((l) => ({ ...l })),
        linked_tickets: ticket.linked_tickets || [],
      }
      this.$emit('updated', ticket)
    },
    async addLink() {
      if (!this.form?.id || !this.newLink.url.trim()) return
      this.linking = true
      this.error = null
      try {
        const { data } = await axios.post(`/api/v1/smm/board/tickets/${this.form.id}/links`, {
          url: this.newLink.url.trim(),
          title: this.newLink.title.trim() || null,
        })
        this.newLink = { url: '', title: '' }
        if (data.ticket) this.applyTicket(data.ticket)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.linking = false
      }
    },
    startEditLink(link) {
      this.editingLinkId = link.id
      this.editingLink = {
        url: link.url || '',
        title: link.title || '',
      }
    },
    cancelEditLink() {
      this.editingLinkId = null
      this.editingLink = { url: '', title: '' }
    },
    async saveEditingLink() {
      if (!this.form?.id || !this.editingLinkId || !this.editingLink.url.trim()) return
      this.linking = true
      this.error = null
      try {
        const { data } = await axios.patch(
          `/api/v1/smm/board/tickets/${this.form.id}/links/${this.editingLinkId}`,
          {
            url: this.editingLink.url.trim(),
            title: this.editingLink.title.trim() || null,
          }
        )
        this.cancelEditLink()
        if (data.ticket) this.applyTicket(data.ticket)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.linking = false
      }
    },
    async removeLink(link) {
      if (!this.form?.id) return
      if (this.editingLinkId === link.id) this.cancelEditLink()
      try {
        const { data } = await axios.delete(
          `/api/v1/smm/board/tickets/${this.form.id}/links/${link.id}`
        )
        if (data.ticket) this.applyTicket(data.ticket)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      }
    },
    onTicketSearch() {
      clearTimeout(this.searchTimer)
      const q = this.ticketSearch.trim()
      if (q.length < 1) {
        this.ticketSearchResults = []
        return
      }
      this.searchTimer = setTimeout(() => this.searchTickets(q), 250)
    },
    async searchTickets(q) {
      if (!this.form?.id) return
      try {
        const { data } = await axios.get('/api/v1/smm/board/tickets/search', {
          params: {
            q,
            project_id: this.form.project_id,
            exclude_id: this.form.id,
          },
        })
        const linkedIds = new Set((this.form.linked_tickets || []).map((i) => i.ticket.id))
        this.ticketSearchResults = (data.data || []).filter((t) => !linkedIds.has(t.id))
      } catch (_) {
        this.ticketSearchResults = []
      }
    },
    async linkTicket(ticket) {
      if (!this.form?.id || !ticket?.id) return
      this.error = null
      try {
        const { data } = await axios.post(
          `/api/v1/smm/board/tickets/${this.form.id}/relations`,
          { related_ticket_id: ticket.id }
        )
        this.ticketSearch = ''
        this.ticketSearchResults = []
        if (data.ticket) this.applyTicket(data.ticket)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      }
    },
    async removeRelation(item) {
      if (!this.form?.id || !item?.relation_id) return
      try {
        const { data } = await axios.delete(
          `/api/v1/smm/board/tickets/${this.form.id}/relations/${item.relation_id}`
        )
        if (data.ticket) this.applyTicket(data.ticket)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      }
    },
    async saveField(field) {
      if (!this.form?.id) return
      this.error = null
      try {
        const payload = { [field]: this.form[field] }
        if (field === 'due_date' && !payload.due_date) payload.due_date = null
        if (field === 'type_id' && !payload.type_id) payload.type_id = null
        const { data } = await axios.patch(`/api/v1/smm/board/tickets/${this.form.id}`, payload)
        this.applyTicket(data.data)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      }
    },
    async saveSection(section) {
      if (!this.form?.id || !section?.id) return
      this.error = null
      try {
        const { data } = await axios.patch(
          `/api/v1/smm/board/tickets/${this.form.id}/sections/${section.id}`,
          { name: section.name, body: section.body }
        )
        if (data.ticket) this.applyTicket(data.ticket)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      }
    },
    async onUpload(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      await this.uploadFiles(files)
    },
    focusAttachZone() {
      this.$refs.attachZone?.focus()
    },
    extractFilesFromDataTransfer(clipboardOrTransfer) {
      const items = clipboardOrTransfer?.items
      if (items?.length) {
        const files = []
        for (const item of items) {
          if (item.kind === 'file') {
            const file = item.getAsFile()
            if (file) files.push(file)
          }
        }
        if (files.length) return files
      }
      return Array.from(clipboardOrTransfer?.files || [])
    },
    onAttachPaste(e) {
      const files = this.extractFilesFromDataTransfer(e.clipboardData)
      if (!files.length) return
      e.preventDefault()
      this.uploadFiles(files)
    },
    onWindowAttachPaste(e) {
      if (!this.show || !this.form?.id) return
      const target = e.target
      const tag = target?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) return
      const files = this.extractFilesFromDataTransfer(e.clipboardData)
      if (!files.length) return
      e.preventDefault()
      this.uploadFiles(files)
    },
    onAttachDrop(e) {
      this.attachDragOver = false
      const files = Array.from(e.dataTransfer?.files || [])
      this.uploadFiles(files)
    },
    async pasteAttachmentsFromClipboard() {
      this.focusAttachZone()
      if (navigator.clipboard?.read) {
        try {
          const clipItems = await navigator.clipboard.read()
          const files = []
          for (const item of clipItems) {
            const type = item.types.find((t) => t.startsWith('image/'))
            if (!type) continue
            const blob = await item.getType(type)
            const ext = String(type).split('/')[1] || 'png'
            files.push(new File([blob], `clipboard-${Date.now()}.${ext}`, { type }))
          }
          if (files.length) {
            await this.uploadFiles(files)
            return
          }
        } catch (_) {
          // fall through
        }
      }
      this.error = this.$t('smm.board.attachments_paste_hint')
    },
    async uploadFiles(files) {
      const list = Array.from(files || []).filter(Boolean)
      if (!list.length || !this.form?.id) return
      this.uploadingFiles = true
      this.error = null
      try {
        const body = new FormData()
        list.forEach((f, i) => body.append(`files[${i}]`, f))
        const { data } = await axios.post(
          `/api/v1/smm/board/tickets/${this.form.id}/files`,
          body,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        if (data.ticket) this.applyTicket(data.ticket)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.uploadingFiles = false
      }
    },
    async removeFile(file) {
      if (!this.form?.id) return
      try {
        const { data } = await axios.delete(
          `/api/v1/smm/board/tickets/${this.form.id}/files/${file.id}`
        )
        if (data.ticket) this.applyTicket(data.ticket)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      }
    },
    async addComment() {
      if (!this.newComment.trim() || !this.form?.id) return
      this.commenting = true
      this.error = null
      try {
        const { data } = await axios.post(
          `/api/v1/smm/board/tickets/${this.form.id}/comments`,
          { body: this.newComment.trim() }
        )
        this.newComment = ''
        if (data.ticket) this.applyTicket(data.ticket)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      } finally {
        this.commenting = false
      }
    },
    async removeComment(comment) {
      if (!this.form?.id) return
      try {
        const { data } = await axios.delete(
          `/api/v1/smm/board/tickets/${this.form.id}/comments/${comment.id}`
        )
        if (data.ticket) this.applyTicket(data.ticket)
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      }
    },
    async deleteTicket() {
      if (!this.form?.id) return
      if (!window.confirm(this.$t('smm.board.delete_confirm'))) return
      try {
        await axios.delete(`/api/v1/smm/board/tickets/${this.form.id}`)
        this.$emit('deleted', this.form.id)
        this.close()
      } catch (e) {
        this.error = this.formatValidationError(e) || this.$t('common.error')
      }
    },
  },
}
</script>
